import { isEmpty } from "lodash-es";
import type { FixtureEntityUpsert, FixturePersonUpsert } from "~pages/Fixtures/types";
import { getIsScoreEqual } from "./getIsScoreEqual";

type UpdateUnion = FixtureEntityUpsert | FixturePersonUpsert;

type ReturnType = {
  awayResultPlace: UpdateUnion["resultPlace"];
  awayResultSecondaryScorePlace: FixturePersonUpsert["resultSecondaryScorePlace"];
  draw: UpdateUnion["draw"];
  homeResultPlace: UpdateUnion["resultPlace"];
  homeResultSecondaryScorePlace: FixturePersonUpsert["resultSecondaryScorePlace"];
};

/** Based on `score` and `secondaryScore` get the data for `draw`, `resultPlace`, `resultSecondaryPlace` as well as processed `score`, `secondaryScore`. */
export const getScoreSubmitData = ({
  awayScore,
  awaySecondaryScore,
  homeScore,
  homeSecondaryScore,
}: {
  awayScore: string;
  awaySecondaryScore: string;
  homeScore: string;
  homeSecondaryScore: string;
}): ReturnType => {
  const isScoreEqual = getIsScoreEqual(homeScore || "0", awayScore || "0");
  const isSecondaryScoreEqual = getIsScoreEqual(homeSecondaryScore || "0", awaySecondaryScore || "0");

  const isScoreNotFilled = isEmpty(homeScore) && isEmpty(awayScore);
  const isSecondaryScoreNotFilled = isEmpty(homeSecondaryScore) && isEmpty(awaySecondaryScore);

  const won = 1 as const;
  const lost = 2 as const;

  /**
   * Written as many objects to make it more readable (and to avoid long lines and complex nested ternary operators) -
   * loads of duplication, but easier to read
   */

  // score fields not completed or at least one of them is empty
  if (isScoreNotFilled) {
    return {
      draw: false,
      awayResultPlace: null,
      awayResultSecondaryScorePlace: null,
      homeResultPlace: null,
      homeResultSecondaryScorePlace: null,
    };
  }

  // home team won
  if (+homeScore > +awayScore) {
    return {
      draw: false,
      awayResultPlace: lost,
      awayResultSecondaryScorePlace: null,
      homeResultPlace: won,
      homeResultSecondaryScorePlace: null,
    };
  }

  // away team won
  if (+homeScore < +awayScore) {
    return {
      draw: false,
      awayResultPlace: won,
      awayResultSecondaryScorePlace: null,
      homeResultPlace: lost,
      homeResultSecondaryScorePlace: null,
    };
  }

  // score fields are equal
  if (isScoreEqual) {
    // secondary score fields not completed or at least one of them is empty - it means draw
    if (isSecondaryScoreNotFilled) {
      return {
        draw: true,
        awayResultPlace: null,
        awayResultSecondaryScorePlace: null,
        homeResultPlace: null,
        homeResultSecondaryScorePlace: null,
      };
    }

    // home team won by secondary score
    if (+homeSecondaryScore > +awaySecondaryScore) {
      return {
        draw: false,
        awayResultPlace: null,
        awayResultSecondaryScorePlace: lost,
        homeResultPlace: null,
        homeResultSecondaryScorePlace: won,
      };
    }

    // away team won by secondary score
    if (+homeSecondaryScore < +awaySecondaryScore) {
      return {
        draw: false,
        awayResultPlace: null,
        awayResultSecondaryScorePlace: won,
        homeResultPlace: null,
        homeResultSecondaryScorePlace: lost,
      };
    }

    // draw by secondary score
    if (isSecondaryScoreEqual) {
      return {
        draw: true,
        awayResultPlace: null,
        awayResultSecondaryScorePlace: null,
        homeResultPlace: null,
        homeResultSecondaryScorePlace: null,
      };
    }
  }

  console.error("unhandled score calculating case", {
    homeScore,
    awayScore,
    homeSecondaryScore,
    awaySecondaryScore,
  });

  return {
    draw: true,
    awayResultPlace: null,
    awayResultSecondaryScorePlace: null,
    homeResultPlace: null,
    homeResultSecondaryScorePlace: null,
  };
};
